import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import agentsService from '@/services/agents.service';
import { Agent, AgentFile, AgentPublicationRequest } from '@/types/agents';
import { useToast } from '@/contexts/ToastContext';
import LoadingSpinner from '@/components/LoadingSpinner';
import AgentAvatar from '@/components/AgentAvatar';
import ScopeBadge from '@/components/ScopeBadge';
import AgentFileRow from '@/components/AgentFileRow';
import AgentShareModal from '@/components/AgentShareModal';
import QuotaIndicator from '@/components/QuotaIndicator';
import { isQuotaError, quotaErrorMessage } from '@/utils/quotaErrorToast';

const ACCEPT = '.pdf,.docx,.md,.txt';
const POLL_MS = 3000;

const isPending = (f: AgentFile) => f.status === 'UPLOADED' || f.status === 'PROCESSING';

const AgentConfigPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [files, setFiles] = useState<AgentFile[]>([]);
  const [uploading, setUploading] = useState<{ filename: string; pct: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<AgentPublicationRequest | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refreshFiles = useCallback(async () => {
    if (!id) return;
    const fs = await agentsService.listFiles(id);
    setFiles(fs);
    return fs;
  }, [id]);

  useEffect(() => {
    if (!id) return;
    Promise.all([agentsService.getAgent(id), agentsService.listFiles(id)])
      .then(([a, fs]) => {
        setAgent(a);
        setFiles(fs);
      })
      .catch(() => showToast('Não foi possível carregar o agente.', 'error'));
    // Pedido pendente (P10) — best-effort, não bloqueia a página.
    agentsService
      .getMyPublicationRequests(id)
      .then((list) => {
        const pending = list.find((r) => r.status === 'PENDING') ?? null;
        setPendingRequest(pending);
      })
      .catch(() => {
        /* silencioso — sem pedido visível */
      });
  }, [id, showToast]);

  // Polling: enquanto algum arquivo estiver na fila/processando, recarrega a lista.
  // (Substitui WS por enquanto — o backend já emite agent:file:status_change pro futuro.)
  useEffect(() => {
    if (!files.some(isPending)) return;
    const timer = setInterval(() => {
      refreshFiles().catch(() => undefined);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [files, refreshFiles]);

  const uploadAll = async (selected: File[]) => {
    if (!id) return;
    for (const file of selected) {
      setUploading({ filename: file.name, pct: 0 });
      try {
        await agentsService.uploadFile(id, file, (pct) => setUploading({ filename: file.name, pct }));
      } catch (err) {
        if (isQuotaError(err)) showToast(quotaErrorMessage(err.response.data), 'error');
        else showToast(`Erro ao subir ${file.name}.`, 'error');
      }
    }
    setUploading(null);
    await refreshFiles();
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length) uploadAll(dropped);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length) uploadAll(selected);
    e.target.value = '';
  };

  const handleDelete = async (file: AgentFile) => {
    if (!id) return;
    try {
      await agentsService.deleteFile(id, file.id);
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
    } catch {
      showToast('Não foi possível excluir o arquivo.', 'error');
    }
  };

  if (!agent) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  const canShare = agent.scope === 'PERSONAL';

  return (
    <div className="max-w-2xl mx-auto p-6 md:p-10" data-testid="agent-config-page">
      <header className="flex items-center gap-4 mb-6">
        <AgentAvatar avatar={agent.avatar} />
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-content-primary truncate">{agent.name}</h1>
          <ScopeBadge scope={agent.scope} className="mt-1" />
        </div>
        <QuotaIndicator />
        {canShare && (
          <button
            type="button"
            data-testid="agent-share"
            onClick={() => setShareOpen(true)}
            disabled={!!pendingRequest}
            title={pendingRequest ? 'Já existe um pedido pendente' : 'Compartilhar com setor ou globalmente'}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-surface-border text-sm text-content-secondary hover:text-accent hover:border-accent transition disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            <span className="material-symbols-outlined text-base">share</span>
            Compartilhar
          </button>
        )}
      </header>

      {pendingRequest && (
        <div
          data-testid="pending-banner"
          className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
        >
          <p className="font-semibold">Aguardando aprovação</p>
          <p className="mt-1">
            Seu pedido para publicar como{' '}
            <strong>{pendingRequest.target_scope === 'GLOBAL' ? 'Global' : 'Setor'}</strong> está
            em análise pelos admins.
            {pendingRequest.target_scope === 'SECTOR' && pendingRequest.target_sectors.length > 0 && (
              <>
                {' '}Setores:{' '}
                <span className="font-medium">
                  {pendingRequest.target_sectors.map((s) => s.name).join(', ')}
                </span>
                .
              </>
            )}
          </p>
        </div>
      )}

      <div
        data-testid="dropzone"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition ${
          dragging ? 'border-accent bg-accent/5' : 'border-surface-border bg-surface-secondary/30'
        }`}
      >
        <span className="material-symbols-outlined text-4xl text-content-tertiary">upload_file</span>
        <p className="mt-2 text-sm text-content-secondary">
          Arraste arquivos aqui ou <span className="text-accent">clique pra selecionar</span>
        </p>
        <p className="text-xs text-content-tertiary mt-1">PDF, DOCX, MD ou TXT</p>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          multiple
          data-testid="file-input"
          className="hidden"
          onChange={onInputChange}
        />
      </div>

      {uploading && (
        <div className="mt-3" data-testid="upload-progress">
          <p className="text-xs text-content-secondary mb-1">
            Enviando {uploading.filename}… {uploading.pct}%
          </p>
          <div className="h-2 rounded bg-surface-tertiary overflow-hidden">
            <div className="h-full bg-accent transition-all" style={{ width: `${uploading.pct}%` }} />
          </div>
        </div>
      )}

      <h2 className="text-lg font-semibold text-content-primary mt-6 mb-2">Arquivos ({files.length})</h2>
      {files.length > 0 ? (
        <ul className="space-y-2" data-testid="file-list">
          {files.map((f) => (
            <AgentFileRow key={f.id} file={f} onDelete={() => handleDelete(f)} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-content-tertiary">Nenhum arquivo ainda. Suba documentos pra alimentar o agente.</p>
      )}

      <div className="mt-8 flex gap-2 justify-end">
        <button
          type="button"
          onClick={() => navigate('/agentes')}
          className="px-4 py-2 rounded-lg text-content-secondary hover:text-content-primary"
        >
          Voltar
        </button>
        <button
          type="button"
          data-testid="open-chat"
          onClick={() => navigate(`/agentes/${id}`)}
          className="inline-flex items-center gap-1 bg-accent text-surface-primary font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition"
        >
          <span className="material-symbols-outlined text-base">chat</span>
          Abrir chat
        </button>
      </div>

      <AgentShareModal
        agent={agent}
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        onSubmitted={(req) => setPendingRequest(req)}
      />
    </div>
  );
};

export default AgentConfigPage;
